export const LOCAL_STATE_VERSION = 1 as const;

export interface LocalProgress {
  readonly version: typeof LOCAL_STATE_VERSION;
  readonly unlockedLevelIds: readonly string[];
}

export interface LocalSettings {
  readonly version: typeof LOCAL_STATE_VERSION;
  readonly language: 'en' | 'ru';
  readonly controlScheme: 'wasd' | 'ijkl';
  readonly accessibility: {
    readonly highContrast: boolean;
    readonly reducedMotion: boolean;
    readonly largeText: boolean;
    readonly captions: boolean;
  };
  readonly audio: {
    readonly muted: boolean;
    readonly masterVolume: number;
    readonly musicVolume: number;
    readonly effectsVolume: number;
  };
}

export type RunOutcome = 'won' | 'lost' | 'abandoned' | 'skipped';
export type PersistenceStatus = 'available' | 'memory-only';
export type StorageProvider = () => Storage;

export interface LocalStateAdapter {
  loadProgress(): LocalProgress;
  recordOutcome(levelId: string, outcome: RunOutcome): LocalProgress;
  loadSettings(): LocalSettings;
  saveSettings(settings: LocalSettings): LocalSettings;
  confirmProgressReset(confirmed: boolean): LocalProgress;
  persistenceStatus(): PersistenceStatus;
}

export const LOCAL_PROGRESS_KEY = 'phasergame.progress.v1';
export const LOCAL_SETTINGS_KEY = 'phasergame.settings.v1';
const LEVEL_ORDER = ['level-1', 'level-2', 'level-3'] as const;

const initialProgress = (): LocalProgress => ({
  version: LOCAL_STATE_VERSION,
  unlockedLevelIds: ['level-1'],
});

const initialSettings = (): LocalSettings => ({
  version: LOCAL_STATE_VERSION,
  language: 'en',
  controlScheme: 'wasd',
  accessibility: {
    highContrast: false,
    reducedMotion: false,
    largeText: false,
    captions: true,
  },
  audio: {
    muted: false,
    masterVolume: 100,
    musicVolume: 80,
    effectsVolume: 100,
  },
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isProgress = (value: unknown): value is LocalProgress => {
  if (!isRecord(value) || value.version !== LOCAL_STATE_VERSION) return false;
  const unlockedLevelIds = value.unlockedLevelIds;
  if (!Array.isArray(unlockedLevelIds)) return false;
  return LEVEL_ORDER.some(
    (_, finalIndex) =>
      unlockedLevelIds.length === finalIndex + 1 &&
      unlockedLevelIds.every((levelId, index) => levelId === LEVEL_ORDER[index]),
  );
};

const isVolume = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 100;

const normalizeSettings = (value: unknown): LocalSettings | undefined => {
  if (
    !isRecord(value) ||
    value.version !== LOCAL_STATE_VERSION ||
    (value.language !== 'en' && value.language !== 'ru') ||
    !isRecord(value.accessibility)
  ) {
    return undefined;
  }
  const accessibility = value.accessibility;
  if (
    !['highContrast', 'reducedMotion', 'largeText', 'captions'].every(
      (key) => typeof accessibility[key] === 'boolean',
    )
  ) {
    return undefined;
  }

  const defaults = initialSettings();
  const controlScheme = value.controlScheme ?? defaults.controlScheme;
  if (controlScheme !== 'wasd' && controlScheme !== 'ijkl') return undefined;
  const audio = value.audio ?? defaults.audio;
  if (
    !isRecord(audio) ||
    typeof audio.muted !== 'boolean' ||
    !isVolume(audio.masterVolume) ||
    !isVolume(audio.musicVolume) ||
    !isVolume(audio.effectsVolume)
  ) {
    return undefined;
  }

  return {
    version: LOCAL_STATE_VERSION,
    language: value.language,
    controlScheme,
    accessibility: {
      highContrast: accessibility.highContrast as boolean,
      reducedMotion: accessibility.reducedMotion as boolean,
      largeText: accessibility.largeText as boolean,
      captions: accessibility.captions as boolean,
    },
    audio: {
      muted: audio.muted,
      masterVolume: audio.masterVolume,
      musicVolume: audio.musicVolume,
      effectsVolume: audio.effectsVolume,
    },
  };
};

const isSettings = (value: unknown): value is LocalSettings => normalizeSettings(value) !== undefined;

const parseSettings = (serialized: string | null): LocalSettings | undefined => {
  if (serialized === null) return undefined;
  try {
    return normalizeSettings(JSON.parse(serialized) as unknown);
  } catch {
    return undefined;
  }
};

const parseRecord = <T>(serialized: string | null, validate: (value: unknown) => value is T): T | undefined => {
  if (serialized === null) return undefined;
  try {
    const value: unknown = JSON.parse(serialized);
    return validate(value) ? value : undefined;
  } catch {
    return undefined;
  }
};

export const createLocalStateAdapter = (provideStorage: StorageProvider): LocalStateAdapter => {
  let status: PersistenceStatus = 'available';
  let progressCache: LocalProgress | undefined;
  let settingsCache: LocalSettings | undefined;

  const useStorage = <T>(operation: (storage: Storage) => T): T | undefined => {
    if (status === 'memory-only') return undefined;
    try {
      return operation(provideStorage());
    } catch {
      status = 'memory-only';
      return undefined;
    }
  };

  const persist = (key: string, value: LocalProgress | LocalSettings): void => {
    useStorage((storage) => storage.setItem(key, JSON.stringify(value)));
  };

  const loadProgress = (): LocalProgress => {
    if (progressCache !== undefined) return progressCache;
    const serialized = useStorage((storage) => storage.getItem(LOCAL_PROGRESS_KEY));
    const storedProgress = parseRecord(serialized ?? null, isProgress);
    progressCache = storedProgress ?? initialProgress();
    if (serialized !== undefined && serialized !== null && storedProgress === undefined) {
      persist(LOCAL_PROGRESS_KEY, progressCache);
    }
    return progressCache;
  };

  const loadSettings = (): LocalSettings => {
    if (settingsCache !== undefined) return settingsCache;
    const serialized = useStorage((storage) => storage.getItem(LOCAL_SETTINGS_KEY));
    const storedSettings = parseSettings(serialized ?? null);
    settingsCache = storedSettings ?? initialSettings();
    if (
      serialized !== undefined &&
      serialized !== null &&
      JSON.stringify(settingsCache) !== serialized
    ) {
      persist(LOCAL_SETTINGS_KEY, settingsCache);
    }
    return settingsCache;
  };

  return {
    loadProgress,
    loadSettings,
    persistenceStatus: () => status,
    saveSettings: (settings) => {
      settingsCache = isSettings(settings) ? normalizeSettings(settings) : initialSettings();
      settingsCache ??= initialSettings();
      persist(LOCAL_SETTINGS_KEY, settingsCache);
      return settingsCache;
    },
    confirmProgressReset: (confirmed) => {
      if (!confirmed) return loadProgress();
      progressCache = initialProgress();
      try {
        provideStorage().removeItem(LOCAL_PROGRESS_KEY);
      } catch {
        status = 'memory-only';
      }
      return progressCache;
    },
    recordOutcome: (levelId, outcome) => {
      const progress = loadProgress();
      if (outcome !== 'won') return progress;

      const completedIndex = LEVEL_ORDER.indexOf(levelId as (typeof LEVEL_ORDER)[number]);
      const nextLevel = LEVEL_ORDER[completedIndex + 1];
      if (
        completedIndex < 0 ||
        !progress.unlockedLevelIds.includes(levelId) ||
        nextLevel === undefined ||
        progress.unlockedLevelIds.includes(nextLevel)
      ) {
        return progress;
      }

      progressCache = {
        version: LOCAL_STATE_VERSION,
        unlockedLevelIds: [...progress.unlockedLevelIds, nextLevel],
      };
      persist(LOCAL_PROGRESS_KEY, progressCache);
      return progressCache;
    },
  };
};

export const LOCAL_STATE = createLocalStateAdapter(() => window.localStorage);